/**
 * buttonIcons — the home-button (`sidebar_link`) icon identifier → component
 * map (US15 / D-122, research D10).
 *
 * The payload stores the proto's icon IDENTIFIER verbatim (one of the 19
 * `BUTTON_ICONS`, content-level proto compatibility); rendering maps each
 * identifier to an omaplus inline-SVG component in `apps/web/src/shell/icons.tsx`
 * — the established hand-rolled convention, NO icon dependency (D-043). Nine of
 * the nineteen reuse existing shell icons (the proto name differs from the
 * omaplus component name: MessageCircle≈ChatBubble, FileText≈Document,
 * Mail≈Envelope); the other ten were added to `icons.tsx` for US15.
 *
 * `resolveButtonIcon` is total: an absent (`null`) or unknown identifier
 * resolves to `null` so the caller renders no icon (the proto's empty option /
 * a forward-compatible unknown), never throwing on member render.
 */

import type { JSX } from 'react';

import {
  AwardIcon,
  BookOpenIcon,
  BriefcaseIcon,
  CalculatorIcon,
  ChatBubbleIcon,
  DocumentIcon,
  EnvelopeIcon,
  GiftIcon,
  GlobeIcon,
  GraduationCapIcon,
  HeartIcon,
  type IconProps,
  LinkIcon,
  LockIcon,
  PhoneIcon,
  PlayIcon,
  ScaleIcon,
  ShieldIcon,
  StarIcon,
  WrenchIcon,
} from '../../shell/icons.js';

/** A shell icon component (an inline `<svg>` taking `IconProps`). */
export type ButtonIconComponent = (props: IconProps) => JSX.Element;

/**
 * The 19 normative `BUTTON_ICONS` identifiers (research D10) → shell icon
 * component. Keyed by the proto identifier; the nine reuse-mappings point at
 * the existing shell components whose omaplus name differs from the proto's.
 */
export const BUTTON_ICON_COMPONENTS: Readonly<Record<string, ButtonIconComponent>> = {
  // Reused existing shell icons (proto name ≈ omaplus component name).
  MessageCircle: ChatBubbleIcon,
  Briefcase: BriefcaseIcon,
  Shield: ShieldIcon,
  Scale: ScaleIcon,
  Gift: GiftIcon,
  FileText: DocumentIcon,
  Globe: GlobeIcon,
  Phone: PhoneIcon,
  Mail: EnvelopeIcon,
  // Added for US15 (drawn to match the proto's rendering).
  Play: PlayIcon,
  GraduationCap: GraduationCapIcon,
  Wrench: WrenchIcon,
  Calculator: CalculatorIcon,
  Lock: LockIcon,
  BookOpen: BookOpenIcon,
  Award: AwardIcon,
  Heart: HeartIcon,
  Star: StarIcon,
  Link: LinkIcon,
};

/**
 * The 19 icon identifiers in proto order (research D10) — the icon picker's
 * option list. Object key order is insertion order, but an explicit array keeps
 * the picker's order independent of the map literal and is the drift-guarded
 * source the parity test pins against the normative set.
 */
export const BUTTON_ICON_IDS = [
  'MessageCircle',
  'Play',
  'GraduationCap',
  'Wrench',
  'Briefcase',
  'Shield',
  'Scale',
  'Gift',
  'Calculator',
  'FileText',
  'Lock',
  'BookOpen',
  'Award',
  'Heart',
  'Star',
  'Globe',
  'Phone',
  'Mail',
  'Link',
] as const;

/** One of the 19 normative icon identifiers (the literal union of `BUTTON_ICON_IDS`). */
export type ButtonIconId = (typeof BUTTON_ICON_IDS)[number];

/**
 * Resolve a stored icon identifier to its component, or `null` when the icon
 * is omitted (`null`) or the identifier is unknown — the caller renders no icon
 * in both cases (the proto's empty option / forward-compatible unknown).
 */
export function resolveButtonIcon(icon: string | null): ButtonIconComponent | null {
  if (icon === null) {
    return null;
  }
  return BUTTON_ICON_COMPONENTS[icon] ?? null;
}
